import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ExternalLink, Github, Brain, BookOpen, Map, Satellite, ArrowRight } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import { projectsData } from '../data/projects';
import SectionHeader from './SectionHeader';
import TiltCard from './TiltCard';

const iconMap = {
  Satellite,
  Brain,
  Map,
  BookOpen,
};

function ProjectCard({ project, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const { isCyberpunk } = useTheme();
  const Icon = iconMap[project.icon] || Satellite;
  const accent = isCyberpunk ? project.borderColor : project.cleanBorderColor;
  const [showWip, setShowWip] = useState(false);

  const handleWipClick = (e) => {
    e.preventDefault();
    if (showWip) return;
    setShowWip(true);
    setTimeout(() => setShowWip(false), 2000);
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="group relative"
      style={{ perspective: '1200px' }}
    >
      <TiltCard className={`relative h-full p-6 transition-all duration-500 overflow-hidden ${
        isCyberpunk
          ? 'bg-cyber-dark/60 neon-border corner-accents hover:bg-cyber-dark/80'
          : 'bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-xl'
      }`}>
        {/* Background gradient on hover */}
        <div className={`absolute inset-0 bg-gradient-to-br ${
          isCyberpunk ? project.gradient : project.cleanGradient
        } opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

        <div className="relative z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div
              className={`w-12 h-12 flex items-center justify-center border transition-colors duration-500 ${
                isCyberpunk ? '' : 'rounded-lg'
              }`}
              style={{ borderColor: `${accent}33`, color: accent }}
            >
              <Icon size={22} />
            </div>
            <div className="flex gap-3">
              {project.github && (
                <a href={project.github} target="_blank" rel="noopener noreferrer"
                  className={`cursor-pointer transition-colors ${
                    isCyberpunk ? 'text-cyber-dim hover:text-cyber-magenta' : 'text-gray-400 hover:text-indigo-600'
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Github size={18} />
                </a>
              )}
              {project.demo && (
                <a href={project.demo} target="_blank" rel="noopener noreferrer"
                  className={`cursor-pointer transition-colors ${
                    isCyberpunk ? 'text-cyber-dim hover:text-cyber-magenta' : 'text-gray-400 hover:text-indigo-600'
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink size={18} />
                </a>
              )}
            </div>
          </div>

          {/* Title */}
          <h3 className={`font-display text-xl font-semibold mb-3 tracking-wide transition-colors duration-500 ${
            isCyberpunk
              ? 'text-white group-hover:text-cyber-magenta'
              : 'text-gray-900 group-hover:text-indigo-600'
          }`}>{project.title}</h3>

          {/* Description */}
          <p className={`font-body text-sm leading-relaxed mb-6 transition-colors duration-500 ${
            isCyberpunk ? 'text-cyber-dim/70' : 'text-gray-600'
          }`}>{project.description}</p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-5">
            {project.tags.map((tag) => (
              <span key={tag} className={`font-mono text-xs px-2 py-1 transition-all duration-500 ${
                isCyberpunk
                  ? 'text-cyber-magenta/60 bg-cyber-magenta/5 border border-cyber-magenta/10'
                  : 'text-indigo-600 bg-indigo-50 rounded-md'
              }`}>{tag}</span>
            ))}
          </div>

          {/* View details link */}
          {project.wip ? (
            <button
              onClick={handleWipClick}
              className={`inline-flex items-center gap-2 font-mono text-xs tracking-wider transition-all duration-300 group/link ${
                showWip
                  ? isCyberpunk ? 'text-cyber-magenta' : 'text-indigo-500'
                  : isCyberpunk ? 'text-cyber-dim/50 hover:text-cyber-magenta' : 'text-gray-400 hover:text-indigo-600'
              }`}
            >
              {showWip
                ? (isCyberpunk ? '// WORK IN PROGRESS //' : 'Work in Progress')
                : (isCyberpunk ? 'VIEW_DETAILS' : 'View Details')
              }
              {!showWip && <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />}
            </button>
          ) : (
            <Link
              to={`/project/${project.id}`}
              className={`inline-flex items-center gap-2 font-mono text-xs tracking-wider transition-all duration-300 group/link ${
                isCyberpunk
                  ? 'text-cyber-dim/50 hover:text-cyber-magenta'
                  : 'text-gray-400 hover:text-indigo-600'
              }`}
            >
              {isCyberpunk ? 'VIEW_DETAILS' : 'View Details'}
              <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
            </Link>
          )}
        </div>

        {/* Bottom accent line */}
        <div
          className="absolute bottom-0 left-0 w-0 h-[2px] group-hover:w-full transition-all duration-500"
          style={{ backgroundColor: accent }}
        />
      </TiltCard>
    </motion.div>
  );
}

export default function Projects() {
  const { isCyberpunk } = useTheme();

  return (
    <section id="projects" className={`relative py-24 px-6 transition-colors duration-700 ${
      isCyberpunk ? 'cyber-grid bg-cyber-black' : 'bg-gray-50'
    }`}>
      <div className="max-w-6xl mx-auto">
        <SectionHeader number="03." title="PROJECTS" />
        <div className="grid md:grid-cols-2 gap-6">
          {projectsData.map((project, i) => (
            <ProjectCard key={project.id} project={project} index={i} />
          ))}
        </div>
      </div>

      {isCyberpunk ? (
        <div className="cyber-divider mt-24 max-w-4xl mx-auto" />
      ) : (
        <div className="mt-24 max-w-4xl mx-auto h-[1px] bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
      )}
    </section>
  );
}
